/**
 * iter 70.5 — Self-profile wrapper that fetches the current user's full
 * org-admin record (with role_ids / department_ids / location_ids) plus the
 * org's locations / departments / roles, then mounts the canonical
 * UserManagerDrawer in self-edit mode.
 *
 * Replaces the retired UserProfileDrawer. Opened from the TopBar avatar menu.
 *
 * Props
 * -----
 *   open       bool
 *   onClose    fn()
 */
import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import UserManagerDrawer from "./OrgAdmin/UserManagerDrawer";

export default function SelfProfileDrawer({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !user?.id || !user?.org_id) return;
    let cancelled = false;
    setErr("");
    Promise.all([
      api.get(`/org-admin/${user.org_id}/users`),
      api.get(`/org-admin/${user.org_id}/locations`),
      api.get(`/org-admin/${user.org_id}/departments`),
      api.get(`/org-admin/${user.org_id}/roles`),
    ])
      .then(([usersR, locsR, deptsR, rolesR]) => {
        if (cancelled) return;
        const me = (usersR.data || []).find((u) => u.id === user.id);
        setBundle({
          me,
          locations: locsR.data || [],
          departments: deptsR.data || [],
          roles: rolesR.data || [],
        });
      })
      .catch((e) => !cancelled && setErr(formatErr(e)));
    return () => { cancelled = true; };
  }, [open, user?.id, user?.org_id]);

  if (!open) return null;

  // While we load, render the drawer empty — the user gets immediate feedback.
  if (err) {
    return (
      <div
        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50"
        data-testid="self-profile-error-overlay"
      >
        <div className="surface p-6 max-w-md">
          <div className="label-xs mb-2">PROFILE LOAD FAILED</div>
          <div className="text-sm text-destructive mb-3" data-testid="self-profile-error">{err}</div>
          <button className="btn btn-secondary" onClick={onClose}>CLOSE</button>
        </div>
      </div>
    );
  }
  if (!bundle?.me) return null;

  return (
    <UserManagerDrawer
      open={open}
      onClose={onClose}
      onSuccess={async () => {
        // Pull the latest /auth/me so the TopBar avatar / role pill refresh.
        try { await refresh?.(); } catch { /* non-blocking */ }
      }}
      mode="edit"
      initialData={bundle.me}
      orgId={user.org_id}
      orgName={user.org_name}
      locations={bundle.locations}
      departments={bundle.departments}
      roles={bundle.roles}
      canDelete={false}
    />
  );
}
